// src/components/NotificationWebsite.js
import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Typography } from 'antd';
import {
  CloseOutlined,
  GiftOutlined,
  ToolOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { API_BASE_URL } from '../api';
import { injectListStyles } from './Profile/MyMessages/listStyles';

const { Title, Text } = Typography;

// ─── 主题配置（对应 category 字段）─────────────────────────────────────────────
const THEMES = {
  info: {
    icon: <InfoCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
    defaultBg: '#e6f7ff',
    borderColor: '#1890ff',
    btnColor: '#1890ff'
  },
  warning: {
    icon: <ToolOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
    defaultBg: '#fffbe6',
    borderColor: '#faad14',
    btnColor: '#faad14'
  },
  festival: {
    icon: <GiftOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
    defaultBg: '#fff0f6',
    borderColor: '#eb2f96',
    btnColor: '#eb2f96'
  }
};

// ─── 工具函数 ──────────────────────────────────────────────────────────────────

// 从 content 对象中取 HTML
const getHtml = (content) => {
  if (!content) return '';
  
  let html = '';
  if (typeof content === 'string') {
    try { html = JSON.parse(content).html || content; } catch { html = content; }
  } else {
    html = content.html || content.text || '';
  }

  // 将相对路径图片补全为完整 URL
  html = html.replace(
    /src="(\/[^"]+)"/g,
    (match, path) => `src="${API_BASE_URL}${path}"`
  );

  return html;
};

// showOnce 逻辑：今天是否已显示
const wasShownToday = (noticeId) => {
  const key = `notice_shown_${noticeId}`;
  const last = localStorage.getItem(key);
  if (!last) return false;
  return new Date(last).toDateString() === new Date().toDateString();
};

const markShownToday = (noticeId) => {
  localStorage.setItem(`notice_shown_${noticeId}`, new Date().toISOString());
};

const isDismissed = (noticeId) =>
  localStorage.getItem(`notice_dismissed_${noticeId}`) === 'true';

const dismiss = (noticeId) =>
  localStorage.setItem(`notice_dismissed_${noticeId}`, 'true');

// ─── 主组件 ────────────────────────────────────────────────────────────────────
const NotificationWebsite = () => {
  const [currentNotice, setCurrentNotice] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    injectListStyles();
    injectAnimationStyles();
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/media/notifications`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const result = await res.json();
      if (!result.success) return;

      // 过滤：只展示未被关闭、且满足 showOnce 条件的通知
      const valid = (result.notifications || []).filter(n => {
        if (isDismissed(n.id)) return false;
        if (n.showOnce && wasShownToday(n.id)) return false;
        return true;
      });

      if (valid.length === 0) return;

      const toShow = valid[0];
      setCurrentNotice(toShow);

      if (toShow.showOnce) markShownToday(toShow.id);
    } catch (err) {
      console.error('获取通知失败:', err);
    }
  };

  const handleClose = () => setCurrentNotice(null);

  const handleDismiss = () => {
    if (currentNotice) dismiss(currentNotice.id);
    setCurrentNotice(null);
  };

  if (!currentNotice) return null;

  return (
    <>
      {currentNotice.style === 'fullscreen'
        ? <FullscreenNotice notice={currentNotice} onClose={handleClose} onDismiss={handleDismiss} />
        : <ModalNotice notice={currentNotice} onClose={handleClose} onDismiss={handleDismiss} />
      }
    </>
  );
};

// ─── 弹窗通知 ─────────────────────────────────────────────────────────────────
const ModalNotice = ({ notice, onClose, onDismiss }) => {
  const contentRef = useRef(null);
  const theme = THEMES[notice.category] || THEMES.info;
  const bgColor = notice.backgroundColor || theme.defaultBg;
  const html = getHtml(notice.content);

  return (
    <>
      {/* 半透明遮罩 */}
      <div
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 9998
        }}
        onClick={onClose}
      />

      {/* 弹窗主体 */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '800px',
        maxWidth: '92vw',
        maxHeight: '80vh',
        zIndex: 9999,
        borderRadius: '12px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        border: `2px solid ${theme.borderColor}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'noticeSlideIn 0.3s ease-out',
        overscrollBehavior: 'contain'
      }}>
        {/* 标题栏 */}
        <div style={{
          background: bgColor,
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          borderBottom: `1px solid ${theme.borderColor}33`,
          flexShrink: 0
        }}>
          {theme.icon}
          <Title level={4} style={{ margin: 0, flex: 1 }}>
            {notice.title}
          </Title>
          <CloseOutlined
            onClick={onClose}
            style={{ cursor: 'pointer', fontSize: '16px', color: '#8c8c8c' }}
            onMouseEnter={e => e.target.style.color = '#262626'}
            onMouseLeave={e => e.target.style.color = '#8c8c8c'}
          />
        </div>

        {/* 富文本内容（可滚动）*/}
        <div
          ref={contentRef}
          className="notification-content-display"
          style={{
            background: bgColor,
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            fontSize: '14px',
            lineHeight: '1.8',
            color: '#333',
            overscrollBehavior: 'contain'
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {/* 底部操作栏 */}
        <div style={{
          background: bgColor,
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: `1px solid ${theme.borderColor}33`,
          flexShrink: 0
        }}>
          <Space>
            <Button
              type="primary"
              onClick={onClose}
              style={{ backgroundColor: theme.btnColor, borderColor: theme.btnColor }}
            >
              我知道了
            </Button>
            <Button type="text" onClick={onDismiss} style={{ color: '#8c8c8c' }}>
              不再显示
            </Button>
          </Space>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
            {notice.endTime && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                有效期至 {new Date(notice.endTime).toLocaleDateString('zh-CN')}
              </Text>
            )}
            {notice.showOnce && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                ⭐ 今天只显示一次
              </Text>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ─── 全屏通知 ─────────────────────────────────────────────────────────────────
const FullscreenNotice = ({ notice, onClose, onDismiss }) => {
  const theme = THEMES[notice.category] || THEMES.info;
  const html = getHtml(notice.content);

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      overflowY: 'auto',
      padding: '60px 24px 40px',
      animation: 'noticeFadeIn 0.4s ease-out'
    }}>
      {/* 关闭按钮 */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: '20px', right: '24px',
          width: '36px', height: '36px', borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: '#fff', fontSize: '16px'
        }}
      >
        <CloseOutlined />
      </div>

      {/* 图标 + 标题 */}
      <div style={{ fontSize: '56px', marginBottom: '16px', color: '#fff' }}>
        {theme.icon}
      </div>
      <Title level={2} style={{ color: '#fff', margin: '0 0 24px', textAlign: 'center' }}>
        {notice.title}
      </Title>

      {/* 富文本内容 */}
      <div
        className="notification-content-display"
        style={{
          maxWidth: '720px',
          width: '100%',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '24px 28px',
          color: '#fff',
          fontSize: '15px',
          lineHeight: '1.9',
          marginBottom: '36px'
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {/* 操作按钮 */}
      <Space size="middle">
        <Button
          size="large"
          onClick={onClose}
          style={{
            backgroundColor: '#fff',
            color: '#667eea',
            border: 'none',
            height: '44px',
            padding: '0 28px',
            fontWeight: 600
          }}
        >
          我知道了
        </Button>
        <Button
          size="large"
          onClick={onDismiss}
          style={{
            backgroundColor: 'transparent',
            color: 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(255,255,255,0.5)',
            height: '44px',
            padding: '0 28px'
          }}
        >
          不再显示
        </Button>
      </Space>

      {notice.showOnce && (
        <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '20px' }}>
          ⭐ 今天只显示一次
        </Text>
      )}
    </div>
  );
};

// ─── 注入动画样式 ──────────────────────────────────────────────────────────────
const injectAnimationStyles = () => {
  const id = 'notification-website-animations';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes noticeSlideIn {
      from { opacity: 0; transform: translate(-50%, -58%) scale(0.95); }
      to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    @keyframes noticeFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    /* 全屏通知内富文本颜色适配白底 */
    .notification-content-display h1,
    .notification-content-display h2,
    .notification-content-display h3 {
      color: inherit;
    }
  `;
  document.head.appendChild(style);
};

export default NotificationWebsite;